import React, { useCallback, useState } from "react";
import { Button, Pagination, Skeleton } from "@mui/material";
import {
  useGetPromptsQuery,
  useUpdatePromptMutation,
} from "@/store/rtk-api/ai-prompts.api";
import { withAdmin } from "@/services/withAdmin";
import { IAdminFireBase } from "@/store/models/admin.models";
import EditPromptModal from "./components/EditPromptModal/EditPromptModal";
import styles from "./AIPrompts.module.scss";
import { IAIPrompt } from "@/store/models/aiPrompts.models";
import { truncateDescription } from "@/helpers/truncateDescr";
import Layout from "@/components/Layout/Layout";

const ITEMS_PER_PAGE = 6;

const AIPromptsCmp: React.FC<{ admin: IAdminFireBase }> = ({ admin }) => {
  const [openModal, setOpenModal] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedPrompt, setSelectedPrompt] = useState<IAIPrompt | null>(null);

  const {
    data: propmtsData,
    isLoading,
    isSuccess,
    refetch,
  } = useGetPromptsQuery({
    page,
    limit: ITEMS_PER_PAGE,
  });

  const [editPrompt] = useUpdatePromptMutation();

  const handleToggleModal = (prompt: IAIPrompt) => {
    setSelectedPrompt(prompt);
    setOpenModal(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleEditPrompt = useCallback(async () => {
    if (selectedPrompt) {
      await editPrompt({
        uid: selectedPrompt.uid,
        prompt: selectedPrompt.prompt,
      });
      setOpenModal(false);
      refetch();
    }
  }, [selectedPrompt, editPrompt, refetch]);

  return (
    <Layout admin={admin} pageNav={true}>
      <div className={styles.cardsWrap}>
        <div className={styles.cards}>
          {isLoading && <Skeleton variant="rounded" />}
          {propmtsData?.data.map((prompt) => (
            <div className={styles.card} key={prompt.uid}>
              <h3 className={styles.cardTitle}>{prompt.title}</h3>
              <div className={styles.cardContent}>
                <p className={styles.cardDescr}>
                  {truncateDescription(prompt.prompt, 190)}
                </p>
                <Button
                  sx={{
                    textTransform: "capitalize",
                    marginBottom: '20px',
                    height: "47px",
                    width: "100%",
                    borderRadius: "12px",
                    color: "#5570F1",
                    "&:hover": {
                      backgroundColor: "#5570F1",
                      color: "#fff",
                    },
                  }}
                  variant="outlined"
                  onClick={() => handleToggleModal(prompt)}
                >
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
        {isSuccess && (
          <Pagination
            page={page}
            onChange={handlePageChange}
            count={Math.ceil(propmtsData.total / ITEMS_PER_PAGE)}
          />
        )}
      </div>

      {openModal && (
        <EditPromptModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          selectedPrompt={selectedPrompt}
          setSelectedPrompt={setSelectedPrompt}
          handleEditPrompt={handleEditPrompt}
        />
      )}
    </Layout>
  );
};

export const AIPrompts = withAdmin(AIPromptsCmp);
AIPromptsCmp.displayName = "AIPromptsCmp";

